import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { identify, track, setUserProperties } from '../utils/mixpanel';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: { full_name?: string }) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  refreshSession: () => Promise<void>;
  checkUserRoleAndRedirect: (userId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('🔄 Getting initial session...');
        
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('❌ Error getting initial session:', error);
        } else {
          console.log('🌐 Initial session from Supabase:', session ? 'Found' : 'Not found', session?.user?.id);
          
          if (session) {
            setUser(session.user);
            // Track user identification
            identify(session.user.id, {
              email: session.user.email,
              created_at: session.user.created_at,
              last_sign_in_at: session.user.last_sign_in_at
            });
          } else {
            console.log('❌ No session found, user will be null');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('❌ Error in getInitialSession:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, session?.user?.id);
        
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          setLoading(false);
          
          // Track authentication events
          track('User Signed In', {
            user_id: session.user.id,
            email: session.user.email,
            provider: session.user.app_metadata?.provider || 'email'
          });
          identify(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
            last_sign_in_at: session.user.last_sign_in_at
          });
          
          // Check if we're on login or auth-callback page and need to redirect
          const currentPath = window.location.pathname;
          if (currentPath === '/login' || currentPath === '/auth-callback') {
            console.log('🔄 User signed in on login/auth-callback page, checking role and redirecting');
            // Add a small delay to ensure user state is fully resolved
            setTimeout(() => {
              checkUserRoleAndRedirect(session.user.id);
            }, 100);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
          track('User Signed Out');
          navigate('/login');
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Only update user if the user object actually changed to prevent unnecessary re-renders
          setUser(prevUser => {
            if (prevUser?.id === session.user.id && 
                prevUser?.email === session.user.email &&
                prevUser?.last_sign_in_at === session.user.last_sign_in_at) {
              console.log('✅ User unchanged, keeping existing user object');
              return prevUser; // No change, return same object to prevent re-renders
            }
            console.log('🔄 User changed, updating user object');
            return session.user; // User changed, update
          });
          setLoading(false);
        } else if (event === 'USER_UPDATED' && session?.user && session.user.created_at === session.user.last_sign_in_at) {
          // This is likely a new user signup
          track('User Signed Up', {
            user_id: session.user.id,
            email: session.user.email,
            provider: session.user.app_metadata?.provider || 'email'
          });
          identify(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
            last_sign_in_at: session.user.last_sign_in_at
          });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate]);

  const signIn = async (email: string, password: string, rememberMe?: boolean) => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updateUserProfile = async (data: { full_name?: string }) => {
    try {
      setError(null);
      const { error } = await supabase.auth.updateUser({
        data
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGithub = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`
        }
      });
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const refreshSession = async () => {
    try {
      setError(null);
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (session) {
        setUser(session.user);
      } else {
        setUser(null);
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  // Check if user is a team member and redirect appropriately
  const checkUserRoleAndRedirect = async (userId: string) => {
    try {
      console.log('Checking user role and redirecting for user ID:', userId);
      
      // Check if user is a team member (has a role assigned)
      const { data: userRoleData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();
      
      console.log('User role query result:', { userRoleData, userError });
      
      if (userError) {
        console.error('Error checking user role:', userError);
        // If there's an error checking the role, default to subscription page
        navigate('/subscription', { replace: true });
        return;
      }
      
      // If user is a team member (has a role), redirect to admin dashboard
      if (userRoleData && userRoleData.role) {
        console.log('User is a team member, redirecting to admin dashboard');
        navigate('/admin', { replace: true });
      } else {
        console.log('User is not a team member, checking subscription status');
        // User is not a team member, check if they have a subscription
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('user_subscriptions')
          .select('status')
          .eq('user_id', userId)
          .eq('status', 'active')
          .single();
        
        if (subscriptionError) {
          console.error('Error checking subscription:', subscriptionError);
          // If there's an error checking subscription, default to subscription page
          navigate('/subscription', { replace: true });
          return;
        }
        
        if (subscriptionData) {
          // User has an active subscription, redirect to dashboard
          console.log('User has active subscription, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          // User has no active subscription, redirect to subscription page
          console.log('User has no active subscription, redirecting to subscription page');
          navigate('/subscription', { replace: true });
        }
      }
    } catch (error) {
      console.error('Error in checkUserRoleAndRedirect:', error);
      // Default to subscription page if there's an unexpected error
      navigate('/subscription', { replace: true });
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    signInWithGoogle,
    signInWithGithub,
    refreshSession,
    checkUserRoleAndRedirect
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 